/* ÉBORA — capa de experiencia.

   El contenido y las obras viven en HTML. Este módulo solo añade dos capas
   prescindibles: una entrada editorial con GSAP y un campo de pigmento con
   Three.js. Si cualquiera falla, la página conserva exactamente el mismo
   contenido, enlaces y scroll.
*/

const VERTEX = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position, 1.0);
  }
`;

const FRAGMENT = `
  precision mediump float;
  varying vec2 vUv;
  uniform vec2 uSize;
  uniform vec2 uPointer;
  uniform float uTime;
  uniform vec3 uInk;
  uniform vec3 uAccent;

  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
  }

  float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x),
               mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), f.x), f.y);
  }

  float field(vec2 uv, vec2 center, vec2 scale, float edge) {
    vec2 d = (uv - center) * scale;
    float wobble = noise(d * 2.1 + uTime * 0.025) * 0.16;
    return 1.0 - smoothstep(edge - 0.2, edge + 0.22, length(d) + wobble);
  }

  void main() {
    vec2 uv = vUv;
    float aspect = uSize.x / max(uSize.y, 1.0);
    vec2 p = uPointer * vec2(0.025, 0.018);

    float washA = field(uv, vec2(0.80, 0.72) + p, vec2(1.15, 1.0), 0.42);
    float washB = field(uv, vec2(0.63, 0.22) - p * 0.8, vec2(1.45, 1.15), 0.32);
    float washC = field(uv, vec2(0.96, 0.38) + p.yx, vec2(2.0, 1.35), 0.30);
    float paper = (noise(uv * vec2(110.0 * aspect, 110.0) + uTime * 0.01) - 0.5) * 0.07;

    vec3 color = uInk * washA * 0.34;
    color += uAccent * washB * 0.30;
    color += mix(uInk, uAccent, 0.5) * washC * 0.16;
    float alpha = clamp((washA + washB + washC) * 0.32 + paper, 0.0, 0.48);
    gl_FragColor = vec4(color, alpha);
  }
`;

function rgbVariable(element, name, fallback) {
  try {
    const values = getComputedStyle(element).getPropertyValue(name).trim().split(',').map(Number);
    if (values.length >= 3 && values.slice(0, 3).every(Number.isFinite)) {
      return values.slice(0, 3).map((value) => value / 255);
    }
  } catch (error) {
    /* El esquema no expone el color: se usa la reserva. */
  }
  return fallback;
}

function montarInteraccion(stage) {
  const estudio = stage.querySelector('[data-cs-ebora-estudio]');
  const obras = Array.from(stage.querySelectorAll('[data-cs-ebora-obra]'));
  const reducido = matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!estudio || !obras.length || reducido) return () => {};

  let frame = 0;
  let objetivoX = 0;
  let objetivoY = 0;
  let actualX = 0;
  let actualY = 0;

  const pintar = () => {
    actualX += (objetivoX - actualX) * 0.09;
    actualY += (objetivoY - actualY) * 0.09;
    obras.forEach((obra, indice) => {
      const profundidad = (indice + 1) * 0.62;
      obra.style.setProperty('--ebora-x', `${actualX * profundidad}px`);
      obra.style.setProperty('--ebora-y', `${actualY * profundidad}px`);
    });
    if (Math.abs(actualX - objetivoX) > 0.02 || Math.abs(actualY - objetivoY) > 0.02) {
      frame = requestAnimationFrame(pintar);
    } else {
      frame = 0;
    }
  };

  const alMover = (evento) => {
    const caja = estudio.getBoundingClientRect();
    objetivoX = ((evento.clientX - caja.left) / caja.width - 0.5) * 5;
    objetivoY = ((evento.clientY - caja.top) / caja.height - 0.5) * 5;
    if (!frame) frame = requestAnimationFrame(pintar);
  };

  const alSalir = () => {
    objetivoX = 0;
    objetivoY = 0;
    if (!frame) frame = requestAnimationFrame(pintar);
  };

  estudio.addEventListener('pointermove', alMover, { passive: true });
  estudio.addEventListener('pointerleave', alSalir, { passive: true });

  if (window.csLib) {
    window.csLib.pide('gsap').then(() => {
      if (!window.gsap || !stage.isConnected) return;
      window.gsap.from(obras, {
        autoAlpha: 0,
        scale: 0.94,
        duration: 1.05,
        stagger: 0.1,
        ease: 'power3.out',
        clearProps: 'opacity,visibility,scale',
      });
      window.gsap.from(stage.querySelectorAll('.cs-ebora__capitulo, .cs-ebora__seguir'), {
        autoAlpha: 0,
        duration: 0.65,
        stagger: 0.06,
        ease: 'power2.out',
        clearProps: 'opacity,visibility',
      });
    }).catch(() => {});
  }

  return () => {
    if (frame) cancelAnimationFrame(frame);
    estudio.removeEventListener('pointermove', alMover);
    estudio.removeEventListener('pointerleave', alSalir);
  };
}

async function montarPigmento(stage) {
  const canvas = stage.querySelector('[data-cs-ebora-canvas]');
  const urlThree = stage.dataset.three;
  const reducido = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const pequeno = matchMedia('(max-width: 749px)').matches;
  if (!canvas || !urlThree || reducido || pequeno) return () => {};

  const prueba = document.createElement('canvas');
  if (!(prueba.getContext('webgl2') || prueba.getContext('webgl'))) return () => {};

  const THREE = await import(urlThree);
  const renderer = new THREE.WebGLRenderer({
    canvas,
    alpha: true,
    antialias: false,
    powerPreference: 'low-power',
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.25));
  renderer.setClearColor(0x000000, 0);

  const scene = new THREE.Scene();
  const camera = new THREE.Camera();
  const uniforms = {
    uSize: { value: new THREE.Vector2(1, 1) },
    uPointer: { value: new THREE.Vector2(0, 0) },
    uTime: { value: 0 },
    uInk: { value: new THREE.Vector3(...rgbVariable(stage, '--color-foreground', [0.9, 0.82, 0.72])) },
    uAccent: { value: new THREE.Vector3(...rgbVariable(stage, '--cs-barro', [0.66, 0.25, 0.14])) },
  };
  const material = new THREE.ShaderMaterial({
    vertexShader: VERTEX,
    fragmentShader: FRAGMENT,
    uniforms,
    transparent: true,
    depthWrite: false,
  });
  const geometry = new THREE.PlaneGeometry(2, 2);
  scene.add(new THREE.Mesh(geometry, material));

  let activo = false;
  let vivo = true;
  let frame = 0;
  let ultimo = performance.now();
  const pointerObjetivo = new THREE.Vector2(0, 0);

  const medir = () => {
    const caja = stage.getBoundingClientRect();
    const ancho = Math.max(1, Math.round(caja.width));
    const alto = Math.max(1, Math.round(caja.height));
    renderer.setSize(ancho, alto, false);
    uniforms.uSize.value.set(ancho, alto);
  };

  const pintar = (ahora) => {
    if (!vivo || !activo || document.hidden) {
      frame = 0;
      return;
    }
    const delta = Math.min(40, ahora - ultimo);
    ultimo = ahora;
    uniforms.uTime.value += delta / 1000;
    uniforms.uPointer.value.lerp(pointerObjetivo, 0.045);
    renderer.render(scene, camera);
    frame = requestAnimationFrame(pintar);
  };

  const arrancar = () => {
    if (!activo || frame || document.hidden || !vivo) return;
    ultimo = performance.now();
    frame = requestAnimationFrame(pintar);
  };

  const parar = () => {
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
  };

  const alMover = (evento) => {
    const caja = stage.getBoundingClientRect();
    pointerObjetivo.set(
      ((evento.clientX - caja.left) / caja.width - 0.5) * 2,
      -((evento.clientY - caja.top) / caja.height - 0.5) * 2
    );
  };

  const alVisibilidad = () => { if (document.hidden) parar(); else arrancar(); };
  const ojo = new IntersectionObserver(([entrada]) => {
    activo = entrada.isIntersecting;
    if (activo) arrancar(); else parar();
  }, { rootMargin: '160px 0px' });
  const tamano = new ResizeObserver(medir);

  canvas.addEventListener('webglcontextlost', (evento) => {
    evento.preventDefault();
    vivo = false;
    parar();
    stage.classList.remove('is-webgl-ready');
  }, { once: true });
  stage.addEventListener('pointermove', alMover, { passive: true });
  document.addEventListener('visibilitychange', alVisibilidad);
  ojo.observe(stage);
  tamano.observe(stage);
  medir();
  renderer.render(scene, camera);
  stage.classList.add('is-webgl-ready');

  return () => {
    vivo = false;
    parar();
    ojo.disconnect();
    tamano.disconnect();
    stage.removeEventListener('pointermove', alMover);
    document.removeEventListener('visibilitychange', alVisibilidad);
    geometry.dispose();
    material.dispose();
    renderer.dispose();
  };
}

(async function iniciar() {
  const stage = document.querySelector('[data-cs-ebora-stage]');
  if (!stage) return;

  const limpiarInteraccion = montarInteraccion(stage);
  let limpiarPigmento = () => {};
  try {
    limpiarPigmento = await montarPigmento(stage);
  } catch (error) {
    stage.classList.remove('is-webgl-ready');
  }

  document.addEventListener('shopify:section:unload', (evento) => {
    if (!evento.target.contains(stage) && evento.target !== stage) return;
    limpiarInteraccion();
    limpiarPigmento();
  }, { once: true });
})();
